import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, FileText, Video, Image, File, ExternalLink, FolderOpen, Mic, BookOpen, Youtube, Instagram } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { timeAgo } from '../utils/timeAgo';
import VideoEmbed from '../components/VideoEmbed';

const Resources = () => {
  const [section, setSection] = useState('resources'); // resources | omm | brochures | shorts | instagram
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [ommVideos, setOmmVideos] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [instaPosts, setInstaPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (section === 'resources') {
      fetchResources();
      fetchCategories();
    } else if (section === 'omm') {
      fetchOmmVideos();
    } else if (section === 'brochures') {
      fetchBrochures();
    } else if (section === 'shorts') {
      fetchShorts();
    } else if (section === 'instagram') {
      fetchInstagramPosts();
    }
  }, [selectedCategory, section]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const { data } = await api.get(`/resources${params}`);
      setResources(data);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/resources/categories');
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchOmmVideos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/omm-videos');
      setOmmVideos(data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrochures = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/brochures');
      setBrochures(data);
    } catch (error) {
      toast.error('Failed to load brochures');
    } finally {
      setLoading(false);
    }
  };

  const fetchShorts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/youtube-shorts');
      setShorts(data);
    } catch (error) {
      toast.error('Failed to load YouTube shorts');
    } finally {
      setLoading(false);
    }
  };

  const trackShortView = (id) => {
    api.post(`/youtube-shorts/${id}/view`).catch(() => {});
  };

  const fetchInstagramPosts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/instagram-posts');
      setInstaPosts(data);
    } catch (error) {
      toast.error('Failed to load Instagram posts');
    } finally {
      setLoading(false);
    }
  };

  const trackInstaView = (id) => {
    api.post(`/instagram-posts/${id}/view`).catch(() => {});
  };

  const openBrochure = async (b) => {
    api.post(`/brochures/${b.id}/download`).catch(() => {});
    try {
      let urlToOpen = b.file_url;
      // Chrome/Safari/mobile browsers block `data:` URL navigation. Convert to blob URL.
      if (b.file_url?.startsWith('data:')) {
        const res = await fetch(b.file_url);
        const blob = await res.blob();
        urlToOpen = URL.createObjectURL(blob);
        // Revoke after a minute so the browser has time to render the PDF.
        setTimeout(() => URL.revokeObjectURL(urlToOpen), 60000);
      }
      const win = window.open(urlToOpen, '_blank', 'noopener');
      if (!win) {
        // Popup blocked — fall back to anchor click which is more permissive.
        const a = document.createElement('a');
        a.href = urlToOpen;
        a.target = '_blank';
        a.rel = 'noopener';
        a.download = b.file_name || `${b.title}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      toast.error('Could not open the brochure. Please try downloading instead.');
    }
  };

  const handleDownload = async (resource) => {
    try {
      await api.post(`/resources/${resource.id}/download`);
      if (resource.file_url) {
        const link = document.createElement('a');
        link.href = resource.file_url;
        link.download = resource.file_name || 'download';
        link.click();
      } else if (resource.external_link) {
        window.open(resource.external_link, '_blank');
      }
      toast.success('Download started');
      fetchResources();
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const trackView = (id) => {
    api.post(`/omm-videos/${id}/view`).catch(() => {});
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <File className="h-6 w-6" />;
    const type = fileType.toLowerCase();
    if (type.includes('pdf') || type.includes('doc')) return <FileText className="h-6 w-6" />;
    if (type.includes('video') || type.includes('mp4')) return <Video className="h-6 w-6" />;
    if (type.includes('image') || type.includes('png') || type.includes('jpg')) return <Image className="h-6 w-6" />;
    return <File className="h-6 w-6" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Sales Scripts': 'bg-blue-100 text-blue-700',
      'Training Materials': 'bg-green-100 text-green-700',
      'Templates': 'bg-purple-100 text-purple-700',
      'Videos': 'bg-red-100 text-red-700',
      'Presentations': 'bg-yellow-100 text-yellow-700',
      'default': 'bg-slate-100 text-slate-700',
    };
    return colors[category] || colors.default;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="resources-container">
            {/* Hero */}
            <div className="mb-8 relative overflow-hidden rounded-lg bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] p-8 text-white">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <FolderOpen className="h-10 w-10 text-[#22C55E]" />
                  <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
                    Tools & Resources
                  </h1>
                </div>
                <p className="text-lg text-blue-100 max-w-2xl">
                  Access exclusive training materials, templates, tools and One Minute Mentor lessons curated for GBA Alumni
                </p>
              </div>
            </div>

            {/* Section Switch */}
            <div className="mb-6">
              <Tabs value={section} onValueChange={setSection}>
                <TabsList className="bg-white">
                  <TabsTrigger value="resources" data-testid="section-resources">
                    <FolderOpen className="h-4 w-4 mr-2" /> Resources
                  </TabsTrigger>
                  <TabsTrigger value="omm" data-testid="section-omm">
                    <Mic className="h-4 w-4 mr-2" /> One Minute Mentor
                  </TabsTrigger>
                  <TabsTrigger value="brochures" data-testid="section-brochures">
                    <BookOpen className="h-4 w-4 mr-2" /> Brochures
                  </TabsTrigger>
                  <TabsTrigger value="shorts" data-testid="section-shorts">
                    <Youtube className="h-4 w-4 mr-2" /> YouTube
                  </TabsTrigger>
                  <TabsTrigger value="instagram" data-testid="section-instagram">
                    <Instagram className="h-4 w-4 mr-2" /> Instagram
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {section === 'resources' && (
              <>
                {/* Category Filters */}
                <div className="mb-6">
                  <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                    <TabsList className="bg-white flex-wrap h-auto">
                      <TabsTrigger value="all" data-testid="category-all">All Resources</TabsTrigger>
                      {categories.map((cat) => (
                        <TabsTrigger key={cat} value={cat} data-testid={`category-${cat}`}>
                          {cat}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>

                {loading ? (
                  <div className="text-center py-12"><p className="text-slate-500">Loading resources...</p></div>
                ) : resources.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <FolderOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-oswald text-xl font-bold text-slate-900 mb-2">No Resources Yet</h3>
                    <p className="text-slate-600">Check back soon! Admins will be adding valuable resources for the community.</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {resources.map((resource, index) => (
                      <motion.div
                        key={resource.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        data-testid="resource-card"
                      >
                        <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all p-6">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="p-3 bg-[#1E3A8A] bg-opacity-10 rounded-lg text-[#1E3A8A]">
                              {getFileIcon(resource.file_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-lg text-slate-900 mb-1 line-clamp-2">{resource.title}</h3>
                              <Badge className={getCategoryColor(resource.category)}>{resource.category}</Badge>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mb-4 line-clamp-3">{resource.description}</p>
                          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Download className="h-3 w-3" />
                                {resource.downloads} downloads
                              </span>
                              <span>{timeAgo(resource.created_at)}</span>
                            </div>
                            <Button
                              onClick={() => handleDownload(resource)}
                              className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                              size="sm"
                              data-testid="download-button"
                            >
                              {resource.external_link ? (
                                <><ExternalLink className="h-4 w-4 mr-2" />Access</>
                              ) : (
                                <><Download className="h-4 w-4 mr-2" />Download</>
                              )}
                            </Button>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}

            {section === 'omm' && (
              <>
                {loading ? (
                  <div className="text-center py-12"><p className="text-slate-500">Loading videos...</p></div>
                ) : ommVideos.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <Mic className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-oswald text-xl font-bold text-slate-900 mb-2">No videos yet</h3>
                    <p className="text-slate-600">Bite-sized mentoring videos from the team will appear here soon.</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="omm-videos-grid">
                    {ommVideos.map((v, index) => (
                      <motion.div
                        key={v.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        data-testid="omm-video-card"
                      >
                        <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
                          <div onClick={() => trackView(v.id)}>
                            <VideoEmbed html={v.embed_code} />
                          </div>
                          <div className="p-5">
                            <h3 className="font-semibold text-lg text-slate-900 mb-1">{v.title}</h3>
                            {v.description && (
                              <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap leading-relaxed">{v.description}</p>
                            )}
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span>{timeAgo(v.created_at)}</span>
                              <span>•</span>
                              <span>{v.views || 0} views</span>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}

            {section === 'brochures' && (
              <>
                {loading ? (
                  <div className="text-center py-12"><p className="text-slate-500">Loading brochures...</p></div>
                ) : brochures.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-oswald text-xl font-bold text-slate-900 mb-2">No brochures yet</h3>
                    <p className="text-slate-600">Course brochures and downloadable PDFs from GBA will appear here.</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="brochures-grid">
                    {brochures.map((b, index) => (
                      <motion.div
                        key={b.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        data-testid="brochure-card"
                      >
                        <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all p-6">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="p-3 bg-red-50 rounded-lg shrink-0">
                              <FileText className="h-7 w-7 text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-lg text-slate-900 mb-1 line-clamp-2">{b.title}</h3>
                              {b.description && (
                                <p className="text-sm text-slate-600 line-clamp-3 whitespace-pre-wrap">{b.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Download className="h-3 w-3" /> {b.downloads || 0} opens
                              </span>
                              <span>{timeAgo(b.created_at)}</span>
                            </div>
                            <Button
                              onClick={() => openBrochure(b)}
                              size="sm"
                              className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                              data-testid="open-brochure-button"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" /> View PDF
                            </Button>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}

            {section === 'shorts' && (
              <>
                {loading ? (
                  <div className="text-center py-12"><p className="text-slate-500">Loading YouTube shorts...</p></div>
                ) : shorts.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <Youtube className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-oswald text-xl font-bold text-slate-900 mb-2">No shorts yet</h3>
                    <p className="text-slate-600">Latest YouTube shorts from Gaurav Bhagat Academy will appear here.</p>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shorts-grid">
                    {shorts.map((s, index) => (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        data-testid="short-card"
                      >
                        <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
                          <div
                            onClick={() => trackShortView(s.id)}
                            className="relative w-full bg-black"
                            style={{ paddingBottom: '177.78%' }}
                          >
                            <iframe
                              src={`https://www.youtube.com/embed/${s.video_id}`}
                              title={s.title}
                              className="absolute inset-0 w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-slate-900 mb-2 line-clamp-2 min-h-[2.5rem]">{s.title}</h3>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span>{s.views || 0} views</span>
                              <a
                                href={s.youtube_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-red-600 hover:text-red-700 flex items-center gap-1 font-medium"
                                data-testid="open-on-youtube"
                              >
                                <Youtube className="h-3.5 w-3.5" /> Open
                              </a>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}

            {section === 'instagram' && (
              <>
                {loading ? (
                  <div className="text-center py-12"><p className="text-slate-500">Loading Instagram posts...</p></div>
                ) : instaPosts.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <Instagram className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-oswald text-xl font-bold text-slate-900 mb-2">No Instagram posts yet</h3>
                    <p className="text-slate-600">Latest posts and reels from @gauravbhagatacademy will appear here.</p>
                  </Card>
                ) : (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-slate-600">
                        Latest from <a href="https://www.instagram.com/gauravbhagatacademy/" target="_blank" rel="noopener noreferrer" className="font-semibold text-pink-600 hover:underline">@gauravbhagatacademy</a>
                      </p>
                      <a
                        href="https://www.instagram.com/gauravbhagatacademy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-slate-600 hover:text-pink-600 flex items-center gap-1 font-medium"
                        data-testid="follow-instagram"
                      >
                        <Instagram className="h-4 w-4" /> Follow on Instagram
                      </a>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="instagram-grid">
                      {instaPosts.map((p, index) => (
                        <motion.div
                          key={p.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.05 }}
                          data-testid="instagram-card"
                        >
                          <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
                            <div className="relative w-full bg-slate-100" style={{ height: '540px' }} onClick={() => trackInstaView(p.id)}>
                              <iframe
                                src={p.embed_url}
                                title={`Instagram ${p.kind}`}
                                className="w-full h-full"
                                frameBorder="0"
                                scrolling="no"
                                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                allowFullScreen
                              />
                            </div>
                            <div className="p-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
                              <Badge className={p.kind === 'reel' ? 'bg-pink-100 text-pink-700' : 'bg-purple-100 text-purple-700'}>
                                {p.kind === 'reel' ? 'Reel' : 'Post'}
                              </Badge>
                              <a
                                href={p.instagram_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-pink-600 hover:text-pink-700 flex items-center gap-1 font-medium"
                                data-testid="open-on-instagram"
                              >
                                <Instagram className="h-3.5 w-3.5" /> Open on Instagram
                              </a>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resources;
